import { useState, useEffect } from 'react';

/**
 * useSolarHeatmapData
 * Fetches data from the provided API endpoint and returns a matrix for heatmap.
 * The hook filters machines with category containing 'SOLAR' (case‑insensitive).
 * It extracts the 'kW' series for each hour within the given date range.
 *
 * @param {string} username - Username for the API query.
 * @param {string} start - ISO string start date.
 * @param {string} end - ISO string end date.
 * @param {string} mode - Mode, e.g., 'hour'.
 * @returns {object} { loading, error, timestamps, machineNames, matrix }
 */
export const useSolarHeatmapData = (username, start, end, mode = 'hour') => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timestamps, setTimestamps] = useState([]); // array of hour strings
  const [machineNames, setMachineNames] = useState([]); // row headers
  const [matrix, setMatrix] = useState([]); // 2‑D array: machine x hour => kW value

  useEffect(() => {
    if (!username || !start || !end) return;
    const controller = new AbortController();
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = `http://localhost:3000/api/data?username=${encodeURIComponent(
          username
        )}&mode=${encodeURIComponent(mode)}&start=${encodeURIComponent(
          start
        )}&end=${encodeURIComponent(end)}`;
        const resp = await fetch(url, { signal: controller.signal });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const json = await resp.json();
        // Expect shape { "Date & Time": [...], "machines": { ... } }
        const dates = json['Date & Time'];
        const machines = json.machines || {};
        // Identify solar machines
        const solarMachines = Object.values(machines).filter((m) => {
          const cat = m.category || '';
          return cat.toUpperCase().includes('SOLAR');
        });
        const names = solarMachines.map((m) => m.name || m.id || 'Solar');
        setMachineNames(names);
        // Build timestamps as ISO strings for each hour entry
        const tsLabels = dates.map((dt) => new Date(dt).toLocaleString());
        setTimestamps(tsLabels);
        // Build matrix rows (machine) x columns (hour)
        const rows = solarMachines.map((machine) => {
          // Prefer the 'kW' series if present, otherwise fallback to 'V' or 'A'.
          const series = machine.data?.kW || machine.data?.V || [];
          // Ensure length matches timestamps; pad with null if missing.
          return series.map((val) => (val === null || val === undefined ? null : Number(val)));
        });
        setMatrix(rows);
      } catch (e) {
        if (e.name !== 'AbortError') setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    return () => controller.abort();
  }, [username, start, end, mode]);

  return { loading, error, timestamps, machineNames, matrix };
};
